/* "Company missing (N)" filter toggle + "Set company for many" window for Packing List / Bulk Data history. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Building2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, getAuthHeader } from "@/App";

export const companyMissing = (h) => !(h.sender_company || h.header?.sender_company || "").trim();

const ManyDialog = ({ open, onClose, rows, type, onSaved, testId }) => {
  const [sel, setSel] = useState(() => new Set());
  const [companies, setCompanies] = useState(null);
  const [cid, setCid] = useState("");
  const [busy, setBusy] = useState(false);
  const onOpen = async () => {
    setSel(new Set(rows.map(r => r.id)));
    if (companies) return;
    try { setCompanies((await axios.get(`${API}/companies`, { headers: getAuthHeader() })).data || []); }
    catch { setCompanies([]); toast.error("Could not load companies"); }
  };
  const toggle = (id) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const all = rows.length > 0 && rows.every(r => sel.has(r.id));
  const apply = async () => {
    if (!cid) return toast.error("Pick a company");
    if (!sel.size) return toast.error("Tick at least one document");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/packing-list/doc-company/bulk`, { type, ids: [...sel], company_id: cid }, { headers: getAuthHeader() });
      toast.success(`${r.data.sender_company} set on ${r.data.updated} document${r.data.updated === 1 ? "" : "s"}${r.data.siblings_updated ? ` (+${r.data.siblings_updated} same-invoice)` : ""}`);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save company"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) onOpen(); else onClose(); }}>
      <DialogContent className="max-w-lg" onOpenAutoFocus={onOpen} data-testid={`${testId}-many-dialog`}>
        <DialogHeader><DialogTitle className="text-sm">Set company for many — {rows.length} missing</DialogTitle></DialogHeader>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={all} onChange={() => setSel(all ? new Set() : new Set(rows.map(r => r.id)))} data-testid={`${testId}-many-all`} />
          Select all ({sel.size}/{rows.length})
        </label>
        <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
          {rows.map(r => (
            <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`${testId}-many-row-${r.id}`} />
              <span className="font-mono text-slate-700 w-20 shrink-0">{r.invoice_no || "—"}</span>
              <span className="truncate text-slate-600" title={r.filename}>{r.filename}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid={`${testId}-many-company`}
            className="flex-1 h-9 rounded-md border border-slate-300 px-2 text-sm">
            <option value="">{companies === null ? "Loading companies…" : "Pick company…"}</option>
            {(companies || []).map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` · ${c.gstin}` : ""}</option>)}
          </select>
          <button type="button" onClick={apply} disabled={busy} data-testid={`${testId}-many-apply`}
            className="inline-flex items-center gap-1.5 h-9 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 px-4 text-xs font-semibold text-white transition-colors">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Apply to {sel.size}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CompanyMissingTools = ({ rows, on, toggle, type, onSaved, testId }) => {
  const [open, setOpen] = useState(false);
  const missing = rows.filter(companyMissing);
  return (
    <>
      <button type="button" onClick={toggle} data-testid={`${testId}-company-missing-filter`}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? "bg-rose-600 border-rose-600 text-white" : "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
        <AlertTriangle className="w-3 h-3" /> Company missing ({missing.length})
      </button>
      {missing.length > 0 && (
        <button type="button" onClick={() => setOpen(true)} data-testid={`${testId}-company-many-btn`}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-indigo-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors">
          <Building2 className="w-3 h-3" /> Set company for many
        </button>
      )}
      {open && <ManyDialog open={open} onClose={() => setOpen(false)} rows={missing} type={type} onSaved={onSaved} testId={testId} />}
    </>
  );
};
